import React from 'react';
import { 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  Paper, 
  Typography, 
  Box, 
  Chip,
  Checkbox,
  Button,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import { usePdf } from '../../context/PdfContext';
import FlattenToggle from './FlattenToggle';

const FileList: React.FC = () => {
  const { 
    files, 
    removeFile, 
    currentFileId, 
    setCurrentFileId,
    toggleFileSelection,
    selectAllFiles,
    deselectAllFiles,
    processSelectedFiles,
    isProcessingBatch
  } = usePdf();

  if (files.length === 0) {
    return null;
  }

  const selectedCount = files.filter(file => file.selected).length;
  const allSelected = selectedCount === files.length && files.length > 0;

  const handleSelectAll = () => {
    if (allSelected) {
      deselectAllFiles();
    } else {
      selectAllFiles();
    }
  };

  const handleProcessSelected = () => {
    processSelectedFiles();
  };

  return (
    <Paper sx={{ mb: 3 }}>
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Uploaded Files</Typography>
          <Stack direction="row" spacing={1}>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleSelectAll}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
            <Button 
              size="small" 
              variant="contained" 
              onClick={handleProcessSelected}
              disabled={selectedCount === 0 || isProcessingBatch}
            >
              Process Selected
            </Button>
          </Stack>
        </Stack>
      </Box>
      <List>
        {files.map((file) => (
          <ListItem
            key={file.id}
            onClick={(e) => {
              // Only change current file if not clicking on checkbox
              if (!(e.target as HTMLElement).closest('.MuiCheckbox-root')) {
                setCurrentFileId(file.id);
              }
            }}
            sx={{
              borderLeft: file.id === currentFileId ? '4px solid #1976d2' : 'none',
              bgcolor: file.id === currentFileId ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              cursor: 'pointer',
              fontWeight: file.id === currentFileId ? 'bold' : 'normal',
              flexDirection: 'column',
              alignItems: 'flex-start',
              paddingBottom: 1,
              overflow: 'hidden',
              width: '100%'
            }}
            secondaryAction={
              <IconButton edge="end" aria-label="delete" onClick={() => removeFile(file.id)}>
                <DeleteIcon />
              </IconButton>
            }
          >
            {/* Main file info row */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: '100%', 
              mb: 1,
              overflow: 'hidden',
              minWidth: 0
            }}>
              <Checkbox
                edge="start"
                checked={!!file.selected}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFileSelection(file.id);
                }}
                className="MuiCheckbox-root"
              />
              <DescriptionIcon sx={{ mr: 2, color: 'primary.main' }} />
              <ListItemText
                primary={file.file.name}
                secondary={`${(file.file.size / 1024).toFixed(2)} KB`}
                sx={{ 
                  flex: '1 1 auto', 
                  minWidth: 0,
                  '& .MuiListItemText-primary': {
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal'
                  },
                  '& .MuiListItemText-secondary': {
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal'
                  }
                }}
              />
              {file.isProcessing && (
                <Chip
                  label="Processing"
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1 }}
                />
              )}
              {file.error && (
                <Chip
                  label="Error"
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1 }}
                />
              )}
            </Box>
            
            {/* Controls row - separate from filename */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              width: '100%',
              pl: 7 // Align with the content after checkbox
            }}>
              <FlattenToggle file={file} />
            </Box>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

export default FileList;
